/**
 * Tax helpers — single source of truth for tax-related math across the app.
 *
 * The system stores customer-facing totals (e.g. `jobs.invoice_total`) as gross
 * (tax-inclusive) figures. Net (ex-tax) figures are derived using the company's
 * tax_rate at the moment of supply (via per-row snapshots on jobs/estimates/invoices)
 * or the live company config when a snapshot is not yet available.
 */

export interface TaxConfig {
  enabled: boolean;
  rate: number;
  label: string;
  inclusive: boolean;
}

export const DEFAULT_TAX_CONFIG: TaxConfig = {
  enabled: true,
  rate: 10,
  label: 'GST',
  inclusive: true,
};

const isOff = (cfg: TaxConfig): boolean => !cfg.enabled || cfg.rate === 0;

/** Net (ex-tax) amount given a gross (tax-inclusive) total. */
export function netFromGross(gross: number, cfg: TaxConfig = DEFAULT_TAX_CONFIG): number {
  if (isOff(cfg)) return gross;
  // Tax-exclusive tenants enter and store amounts ex-tax already (the job
  // financials trigger sets revenue_before_gst = invoice_total for them).
  if (!cfg.inclusive) return gross;
  return gross / (1 + cfg.rate / 100);
}

/** Gross (tax-inclusive) amount given a net (ex-tax) figure. */
export function grossFromNet(net: number, cfg: TaxConfig = DEFAULT_TAX_CONFIG): number {
  if (isOff(cfg)) return net;
  return net * (1 + cfg.rate / 100);
}

/** Tax embedded in a gross figure. */
export function taxFromGross(gross: number, cfg: TaxConfig = DEFAULT_TAX_CONFIG): number {
  if (isOff(cfg)) return 0;
  return gross - netFromGross(gross, cfg);
}

/** Tax owed on top of a net figure. */
export function taxFromNet(net: number, cfg: TaxConfig = DEFAULT_TAX_CONFIG): number {
  if (isOff(cfg)) return 0;
  return net * (cfg.rate / 100);
}

/**
 * "GST (10%)" / "Sales Tax (8.25%)". Returns empty string when tax is off so
 * callers can use it directly in JSX without a separate enabled-check.
 */
export function formatTaxLine(cfg: TaxConfig = DEFAULT_TAX_CONFIG): string {
  if (!cfg.enabled) return '';
  const rate = Number.isInteger(cfg.rate) ? cfg.rate.toString() : cfg.rate.toFixed(2);
  return `${cfg.label} (${rate}%)`;
}

/** "inc. GST" / "ex. Sales Tax". Empty string when tax is off. */
export function formatTaxSuffix(cfg: TaxConfig = DEFAULT_TAX_CONFIG): string {
  if (!cfg.enabled) return '';
  return `${cfg.inclusive ? 'inc.' : 'ex.'} ${cfg.label}`;
}

/** "Tax Invoice" / "Invoice" heading per AU tax-compliance norms. */
export function taxInvoiceHeading(cfg: TaxConfig = DEFAULT_TAX_CONFIG): string {
  return cfg.enabled ? 'Tax Invoice' : 'Invoice';
}

/**
 * Resolve a TaxConfig from a per-row snapshot, falling back to live company
 * settings (or AU defaults if the row has no snapshot and no company is loaded).
 *
 * Rows store snapshots once Step-1 is deployed; any row created before snapshots
 * existed reads the live company values via the second argument.
 */
type LiveCompanyTax = {
  tax_enabled?: boolean | null;
  tax_rate?: number | null;
  tax_label?: string | null;
  tax_inclusive?: boolean | null;
};

/** Accept either raw company columns or an already-normalised TaxConfig (what useCompanyTax returns). */
function normaliseLive(live: LiveCompanyTax | Partial<TaxConfig> | null | undefined): LiveCompanyTax {
  if (!live) return {};
  if ('enabled' in live || 'rate' in live || 'label' in live || 'inclusive' in live) {
    const cfg = live as Partial<TaxConfig>;
    return { tax_enabled: cfg.enabled, tax_rate: cfg.rate, tax_label: cfg.label, tax_inclusive: cfg.inclusive };
  }
  return live as LiveCompanyTax;
}

export function resolveTaxConfig(
  snapshot: {
    tax_rate_snapshot?: number | null;
    tax_label_snapshot?: string | null;
    tax_inclusive_snapshot?: boolean | null;
  } | null | undefined,
  liveCompany?: LiveCompanyTax | Partial<TaxConfig> | null,
): TaxConfig {
  // Callers pass either the company row or the result of useCompanyTax();
  // previously the latter was silently ignored, so a tenant with tax disabled
  // still saw a tax line on every document.
  const live = normaliseLive(liveCompany);
  const rate = snapshot?.tax_rate_snapshot ?? live.tax_rate ?? DEFAULT_TAX_CONFIG.rate;
  const label = snapshot?.tax_label_snapshot ?? live.tax_label ?? DEFAULT_TAX_CONFIG.label;
  const inclusive = snapshot?.tax_inclusive_snapshot ?? live.tax_inclusive ?? DEFAULT_TAX_CONFIG.inclusive;
  // `enabled` is only on companies; snapshot rows with rate=0 already behave as disabled.
  const enabled = live.tax_enabled ?? DEFAULT_TAX_CONFIG.enabled;
  return { enabled, rate, label, inclusive };
}
